import { JSX, ReactNode, useEffect } from 'react';
import { CheckIcon } from '@chakra-ui/icons';
import { Box, Button, HStack, Icon, Image, Text, VStack } from '@chakra-ui/react';

import { useHandScannerContext } from '../../../contexts/HandScannerContext';
import { useTranslations } from '../../../contexts/LocalizationContext';
import HandScannerHelp from './HandScannerHelp';

const getIconPath = (iconName: string) => require(`../../../assets/icons/${iconName}`);

export type HandScannerProps = {
  // Called when a code is scanned by the hand scanner
  onScan: (data: string) => void;
  // Optional descriptive text shown next to the status
  description?: ReactNode;
  // Optional background color for the connected status card (defaults to white)
  connectedBg?: string;
  showTitle?: boolean;
  fontSize?: string;
};

const HandScanner = ({
  description,
  onScan,
  connectedBg = 'white',
  showTitle = true,
  fontSize,
}: HandScannerProps): JSX.Element => {
  const t = useTranslations();
  const { isConnected, isConnecting, connect, setOnScan } = useHandScannerContext();

  // Register onScan callback in context so scan events bubble up to host
  useEffect(() => {
    setOnScan(onScan);
    return () => setOnScan(null);
  }, [onScan, setOnScan]);

  return (
    <VStack align="stretch" spacing={3} color={'grey.900'}>
      {showTitle ? (
        <HStack justify="space-between" align="center">
          <Text fontWeight="semibold">{t('hand_scanner_title')}</Text>
        </HStack>
      ) : null}

      {description ? (
        <Text fontSize={fontSize} align="left">
          {description}
        </Text>
      ) : (
        <Text fontSize={fontSize} align="left">
          {t('hand_scanner_description')}
        </Text>
      )}

      <HStack align="start">
        {isConnected ? (
          <Box bg={connectedBg} borderRadius="12px" boxShadow="sm" px={6} py={2} display="inline-block">
            <HStack spacing={3}>
              <Box
                boxSize="20px"
                bg="green.600"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={CheckIcon} color="white" boxSize={3} />
              </Box>
              <Text fontSize={fontSize}>{t('hand_scanner_connected')}</Text>
            </HStack>
          </Box>
        ) : (
          <VStack align="start" spacing={2} width="100%">
            <Button
              size="md"
              leftIcon={<Image src={getIconPath('bluetooth.svg')} boxSize={4} alt={t('hand_scanner_bluetooth_alt')} />}
              bg="blue.600"
              color="white"
              _hover={{ bg: 'blue.700' }}
              _active={{ bg: 'blue.600' }}
              onClick={connect}
              isLoading={isConnecting}
              loadingText={t('hand_scanner_connecting')}
              fontSize={fontSize}
            >
              {t('hand_scanner_connect_cta')}
            </Button>
            <Box mt={2}>
              <HandScannerHelp />
            </Box>
          </VStack>
        )}
      </HStack>
    </VStack>
  );
};

export default HandScanner;
